import React from 'react';
import { Column } from '@tanstack/react-table';
import { CalendarIcon } from 'lucide-react';
import { formatDate } from 'utils/custom-date';
import { useIsMobile } from 'hooks/use-mobile';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Button } from 'components/ui/button';
import { Separator } from 'components/ui/separator';
import usePopoverWidth from 'hooks/use-popover-width';
import { Calendar } from 'components/ui/calendar';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'components/core/theme-provider';

/**
 * DateRangeFilter Component
 *
 * A filter component that allows users to select a date range from a calendar.
 * This component integrates with `@tanstack/react-table` to filter data based on the selected date range.
 * It supports both desktop and mobile views, with responsive layouts and popover behavior.
 *
 * Features:
 * - Allows users to select a range of dates using a calendar UI.
 * - Displays the selected date range in the filter button.
 * - Clears the selected date range with a "Clear filter" button.
 * - Integrates with `@tanstack/react-table` to filter data based on the selected date range.
 * - Supports mobile-friendly views with responsive layouts.
 *
 * @template TData - The type of data used in the table.
 * @template TValue - The type of value for the column being filtered.
 *
 * @param {Column<TData, TValue>} [column] - The column to be filtered, passed from `@tanstack/react-table`.
 * @param {string} title - The title to be displayed for the date range filter.
 * @param {DateRange | undefined} date - The current selected date range.
 * @param {(date: DateRange | undefined) => void} onDateChange - Callback to handle changes to the selected date range.
 *
 * @returns {JSX.Element} A date range filter component.
 */

interface DateRangeFilterProps<TData, TValue> {
  column?: Column<TData, TValue>;
  title: string;
  date: DateRange | undefined;
  onDateChange: (date: DateRange | undefined) => void;
}

export function DateRangeFilter<TData, TValue>({
  column,
  title,
  date,
  onDateChange,
}: Readonly<DateRangeFilterProps<TData, TValue>>) {
  const isMobile = useIsMobile();
  const [buttonRef] = usePopoverWidth();
  const [open, setOpen] = React.useState(false);
  const [localDateRange, setLocalDateRange] = React.useState<DateRange | undefined>(date);
  const [month, setMonth] = React.useState<Date>(date?.from || new Date());
  const { t } = useTranslation();
  const theme = useTheme();

  React.useEffect(() => {
    setLocalDateRange(date);
    if (date?.from) {
      setMonth(date.from);
    }
  }, [date]);

  const handleDateSelect = (selectedDateRange: DateRange | undefined) => {
    setLocalDateRange(selectedDateRange);
    onDateChange(selectedDateRange);

    if (selectedDateRange?.from && selectedDateRange?.to) {
      // Convert dates to UTC with proper time boundaries
      const fromDate = new Date(selectedDateRange.from);
      const toDate = new Date(selectedDateRange.to);

      // Set 'from' to next day at 00:00:01 UTC
      const utcFromDate = new Date(Date.UTC(
        fromDate.getFullYear(),
        fromDate.getMonth(),
        fromDate.getDate(), // Next day
        0, 0, 1 // 00:00:01
      ));

      // Set 'to' to same day at 23:59:59 UTC
      const utcToDate = new Date(Date.UTC(
        toDate.getFullYear(),
        toDate.getMonth(),
        toDate.getDate(),
        23, 59, 59 // 23:59:59
      ));

      column?.setFilterValue({
        from: utcFromDate,
        to: utcToDate
      });
    } else if (!selectedDateRange?.from) {
      column?.setFilterValue(undefined);
    }
  };

  const clearFilter = (e: React.MouseEvent) => {
    e.stopPropagation();

    setLocalDateRange(undefined);
    onDateChange(undefined);
    column?.setFilterValue(undefined);
    setMonth(new Date());
    setOpen(false);
  };

  const handlePopoverKeyDown = (e: React.KeyboardEvent) => {
    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.stopPropagation();
    }
  };

  const hasActiveFilter = localDateRange?.from != null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={buttonRef}
          variant={hasActiveFilter ? 'default' : 'outline'}
          size="sm"
          className={`h-10 rounded-3xl border ${hasActiveFilter ? '' : 'text-primary'} border-primary border}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <CalendarIcon className="mr-2 h-4 w-4" />
              <span>{t(title)}</span>
            </div>
            {localDateRange?.from && (
              <>
                <Separator orientation="vertical" className="hidden h-4 sm:mx-2 sm:block" />
                <span className="truncate ml-2">
                  {formatDate(localDateRange.from, true)}
                  {localDateRange.to && (
                    <>
                      {' - '}
                      {formatDate(localDateRange.to, true)}
                    </>
                  )}
                </span>
              </>
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        sideOffset={8}
        onKeyDown={handlePopoverKeyDown}
        style={{
          width: '100%',
          maxWidth: '100vw',
          background: 'white',
        }}
      >
        <button
          className="flex flex-col"
          onClick={(e) => {
            e.stopPropagation();
          }}
          type="button"
        >
          <Calendar
            initialFocus
            mode="range"
            month={month}
            onMonthChange={setMonth}
            defaultMonth={localDateRange?.from || new Date()}
            selected={localDateRange || { from: new Date(), to: undefined }}
            onSelect={handleDateSelect}
            numberOfMonths={isMobile ? 1 : 2}
            className={`rounded-lg border ${theme.theme === 'dark' ? 'bg-[black]' : 'bg-white'}`}
            classNames={{
              caption_label: theme.theme === 'dark' ? 'text-[#fff]' : 'text-gray-900',
              nav_button: theme.theme === 'dark'
                ? 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-[#fff]'
                : 'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-gray-900',
              nav_button_next: theme.theme === 'dark' ? 'absolute right-1' : 'absolute right-1',
              nav_button_previous: theme.theme === 'dark' ? 'absolute left-1' : 'absolute left-1',
              day: theme.theme === 'dark'
                ? 'h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-[#fff] rounded-lg hover:bg-[gray] hover:text-[#fff]'
                : 'h-9 w-9 p-0 font-normal aria-selected:opacity-100 text-gray-900 rounded-lg hover:bg-[lightgray] hover:text-gray-900',
              day_selected: theme.theme === 'dark'
                ? 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-lg'
                : 'bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground',
              day_outside: theme.theme === 'dark'
                ? 'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30'
                : 'day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30',
              day_disabled: theme.theme === 'dark'
                ? 'text-muted-foreground opacity-50'
                : 'text-muted-foreground opacity-50',
              day_range_middle: theme.theme === 'dark'
                ? 'aria-selected:bg-accent aria-selected:text-accent-foreground rounded-none'
                : 'aria-selected:bg-[lightgray] aria-selected:text-accent-foreground rounded-none',
            }}
          />
          <div className={`p-2 border-t ${theme.theme === 'dark' ? 'bg-[black]' : 'bg-white'}`}>
            <Button variant="ghost" onClick={clearFilter} className={theme.theme === 'dark' ? 'w-full text-[#fff]' : 'w-full'} size="sm">
              {t('CLEAR_FILTER')}
            </Button>
          </div>
        </button>
      </PopoverContent>
    </Popover>
  );
}
